import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';

import { SkButtonComponent } from '@mercadoni/stark-ui';

import type { AppError } from '../../../core/interfaces';
import { translateNow, translationVersion } from '../../../core/services/i18n/translate-now';
import { translated } from '../../utils/translated.util';
import { NfIconComponent } from '../icon/nf-icon.component';

/** How long the "Copiado" confirmation stays on the copy button. */
const COPIED_FEEDBACK_MS = 2000;

/**
 * Inline error alert for a failed operation: the message, the backend's error id (when there is
 * one) and a "Copiar Log ID" button so the admin can pass the id to support.
 *
 * Place it inside the dialog or screen where the operation failed, instead of showing a toast.
 * Pass an {@link AppError} (from `toAppError`) or the individual fields.
 */
@Component({
  selector: 'nf-error-alert',
  imports: [SkButtonComponent, NfIconComponent],
  template: `
    <div
      role="alert"
      class="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded-lg border border-status-error-border bg-status-error-bg px-3 py-2 text-sm text-status-error-fg"
    >
      <div class="flex min-w-0 flex-1 basis-48 items-start gap-2">
        <nf-icon name="triangle-alert" [size]="16" class="mt-0.5 flex-shrink-0" />
        <p class="break-words">
          {{ shownMessage() }}
          @if (shownDetail(); as technical) {
            <span translate="no" class="block font-mono text-xs opacity-80">{{ technical }}</span>
          }
          @if (shownId(); as id) {
            <span translate="no" class="block font-mono text-xs">errorId: {{ id }}</span>
          }
        </p>
      </div>
      @if (shownId()) {
        <sk-button variant="ghost" size="sm" (skClick)="copyErrorId()">
          {{ copied() ? copiedLabel() : copyLabel() }}
        </sk-button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NfErrorAlertComponent {
  /** A normalised failure; when set it supplies the message, detail and id and the inputs below are ignored. */
  readonly error = input<AppError | null>(null);
  /** Text shown to the admin. Not needed when {@link error} is given. */
  readonly message = input<string>('');
  /** Original technical text, shown under the message when the message is a translation of it. */
  readonly detail = input<string | null>(null);
  /** Backend error id to share with support; hides the id and the copy button when absent. */
  readonly errorId = input<string | null>(null);

  protected readonly copied = signal<boolean>(false);
  protected readonly copiedLabel = translated('common.errorAlert.copied');
  protected readonly copyLabel = translated('common.errorAlert.copyLogId');

  protected readonly shownMessage = computed(() => {
    translationVersion(); // a message left on screen is rebuilt when the language changes
    const error = this.error();
    return error?.messageKey ? translateNow(error.messageKey) : (error?.message ?? this.message());
  });
  protected readonly shownDetail = computed(() => this.error()?.detail ?? this.detail());
  protected readonly shownId = computed(() => this.error()?.errorId ?? this.errorId());

  private feedbackTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
    });
  }

  /** Copies the error id to the clipboard; if the browser refuses, the button keeps its label. */
  async copyErrorId(): Promise<void> {
    const id = this.shownId();
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      return;
    }
    this.copied.set(true);
    if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
    this.feedbackTimer = setTimeout(() => this.copied.set(false), COPIED_FEEDBACK_MS);
  }
}
